import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { apiService } from '@/services/apiService';
import { Plus, Pencil, Trash2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    status: 'ativo',
    goal: '',
    agent_id: 'main',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projectsData, tasksData] = await Promise.all([
        apiService.getProjects(),
        apiService.getTasks()
      ]);
      setProjects(projectsData);
      setTasks(tasksData);
    } finally {
      setLoading(false);
    }
  };

  const getProjectProgress = (projectId) => {
    const projectTasks = tasks.filter(t => t.project_id === projectId);
    if (projectTasks.length === 0) return 0;
    const completedTasks = projectTasks.filter(t => t.status === 'done' || t.status === 'concluido').length;
    return Math.round((completedTasks / projectTasks.length) * 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await apiService.updateProject(editingProject.id, formData);
        toast.success('Projeto atualizado');
      } else {
        await apiService.createProject(formData);
        toast.success('Projeto criado');
      }
      setDialogOpen(false);
      setEditingProject(null);
      setFormData({ name: '', status: 'ativo', goal: '', agent_id: 'main' });
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar projeto');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      status: project.status,
      goal: project.goal,
      agent_id: project.agent_id || 'main',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este projeto?')) {
      try {
        await apiService.deleteProject(id);
        toast.success('Projeto excluído');
        loadData();
      } catch (error) {
        toast.error('Erro ao excluir');
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Carregando...</div>;
  }

  return (
    <div data-testid="projects-page">
      <PageHeader
        title="Projetos"
        description="Gerencie seus projetos e acompanhe o progresso"
        actions={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary hover:bg-primary/90 rounded-sm uppercase text-xs tracking-wider"
                data-testid="create-project-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Projeto
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121215] border-border">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  {editingProject ? 'Editar Projeto' : 'Novo Projeto'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-wider">Nome</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="mt-1.5"
                    data-testid="project-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="status" className="text-xs uppercase tracking-wider">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger className="mt-1.5" data-testid="project-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="planejamento">Planejamento</SelectItem>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="producao">Produção</SelectItem>
                      <SelectItem value="concluido">Concluído</SelectItem>
                      <SelectItem value="pausado">Pausado</SelectItem>
                      <SelectItem value="cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="goal" className="text-xs uppercase tracking-wider">Objetivo</Label>
                  <Textarea
                    id="goal"
                    value={formData.goal}
                    onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                    className="mt-1.5"
                    rows={3}
                    data-testid="project-goal-input"
                  />
                </div>
                <div>
                  <Label htmlFor="agent_id" className="text-xs uppercase tracking-wider">Responsável</Label>
                  <Select value={formData.agent_id} onValueChange={(value) => setFormData({ ...formData, agent_id: value })}>
                    <SelectTrigger className="mt-1.5" data-testid="project-agent-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="main">Tom</SelectItem>
                      <SelectItem value="linus">Linus</SelectItem>
                      <SelectItem value="atlas">Atlas</SelectItem>
                      <SelectItem value="sys">SYS</SelectItem>
                      <SelectItem value="fred">Fred</SelectItem>
                      <SelectItem value="mark">Mark</SelectItem>
                      <SelectItem value="iris">Íris</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      setEditingProject(null);
                      setFormData({ name: '', status: 'ativo', goal: '', agent_id: 'main' });
                    }}
                    className="uppercase text-xs"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-primary hover:bg-primary/90 uppercase text-xs"
                    data-testid="submit-project-btn"
                  >
                    {editingProject ? 'Atualizar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Projetos agrupados por status */}
      {(() => {
        const statusGroups = {
          'Em Andamento': projects.filter(p => ['ativo', 'producao', 'em_andamento'].includes(p.status)),
          'Planejamento': projects.filter(p => p.status === 'planejamento'),
          'Pausados': projects.filter(p => ['pausado', 'paused'].includes(p.status)),
          'Concluídos': projects.filter(p => p.status === 'concluido'),
          'Arquivados': projects.filter(p => ['arquivado', 'archived', 'cancelado'].includes(p.status)),
        };

        return Object.entries(statusGroups).map(([groupName, groupProjects]) => {
          if (groupProjects.length === 0) return null;
          return (
            <div key={groupName} className="mb-8">
              <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2">
                {groupName}
                <span className="text-xs font-mono bg-muted/20 px-2 py-0.5 rounded">{groupProjects.length}</span>
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {groupProjects.map((project) => {
                  const progress = getProjectProgress(project.id);
                  return (
                    <div
                      key={project.id}
                      className="rounded-md border border-border/50 bg-card/40 backdrop-blur-sm hover:border-primary/30 transition-all cursor-pointer"
                      onClick={() => navigate(`/projetos/${project.id}`)}
                      data-testid="project-card"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-heading text-lg font-semibold truncate">{project.name}</h3>
                              {(project.status === 'concluido' || progress === 100) && (
                                <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                              )}
                            </div>
                            <StatusBadge status={project.status} className="mt-2" />
                          </div>
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{project.goal}</p>
                        
                        {/* Barra de Progresso */}
                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-xs uppercase text-muted-foreground">Andamento</span>
                            <span className="text-xs font-bold font-mono">{progress}%</span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-background/60 overflow-hidden">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(project);
                            }}
                            className="h-7 w-7 p-0"
                            data-testid="edit-project-btn"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(project.id);
                            }}
                            className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                            data-testid="delete-project-btn"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        });
      })()}

      {projects.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <p>Nenhum projeto encontrado</p>
          <p className="text-sm">Crie seu primeiro projeto para começar</p>
        </div>
      )}
    </div>
  );
}
